import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import { VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import Drawer from 'tio-reimagined/components/drawer';
import Card from 'tio-reimagined/components/card';
import { eq } from 'tio-ui/utilities';
import SectionHeader from 'tio-reimagined/components/section-header';
import SmartActionsSection from 'tio-employee/components/reimagined/smart-actions-section';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import PageHero from 'tio-reimagined/components/page-hero';
import GlassCard from 'tio-reimagined/components/glass-card';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import ExtractionSummary from 'tio-employee/components/observability/extraction-summary';
import type ObservableDocumentModel from 'tio-data/models/observable-document';
import { EXTRACTION_STATES } from 'tio-data/models/observable-document';
import { formatShortDate } from 'tio-reimagined/format';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhFileText,
  PhUploadSimple,
  PhCheckCircle,
  PhWarning,
  PhClock,
  PhShieldCheck,
  PhEye,
  PhCloudArrowUp,
  PhSparkle,
  PhRobot,
  PhDownloadSimple,
  PhArrowSquareOut,
  PhFolderOpen,
} from 'ember-phosphor-icons';

interface DocumentsIndexModel {
  dataSourceInfo: DataSourceInfo;
  observableDocuments: ObservableDocumentModel[];
}

interface DocumentsIndexSignature {
  Args: {
    model: DocumentsIndexModel;
  };
}

interface Document {
  id: string;
  name: string;
  type: string;
  status: 'verified' | 'pending' | 'action-needed';
  uploadDate: string;
  statusLabel: string;
}

const DEMO_DOCUMENTS: Document[] = [
  {
    id: '1',
    name: 'Student Loan Statement - Jan 2026',
    type: 'Loan Statement',
    status: 'verified',
    uploadDate: 'Jan 15, 2026',
    statusLabel: 'Verified',
  },
  {
    id: '2',
    name: 'Employment Certification Form',
    type: 'ECF',
    status: 'verified',
    uploadDate: 'Dec 10, 2025',
    statusLabel: 'Verified',
  },
  {
    id: '3',
    name: 'W-2 Form 2025',
    type: 'Tax Document',
    status: 'pending',
    uploadDate: 'Feb 1, 2026',
    statusLabel: 'Under Review',
  },
  {
    id: '4',
    name: 'Tuition Invoice - Spring 2026',
    type: 'Tuition Document',
    status: 'action-needed',
    uploadDate: 'Jan 28, 2026',
    statusLabel: 'Action Needed',
  },
];

const PROVIDER_LABELS: Record<string, string> = {
  'ObservableProvider.NSLDS': 'NSLDS File',
  'ObservableProvider.ACCOUNT_STATEMENT': 'Loan Statement',
  'ObservableProvider.ACCOUNT_TXN_HISTORY': 'Transaction History',
  'ObservableProvider.PROXY': 'Proxy Upload',
};

function mapExtractionStatus(state: keyof typeof EXTRACTION_STATES): Document['status'] {
  const level = EXTRACTION_STATES[state];
  if (!level) return 'pending';
  if (level >= 4) return 'verified'; // PROCESSED or DERELICT
  if (state === 'ExtractionState.FAILED' || state === 'ExtractionState.INVALID')
    return 'action-needed';
  return 'pending'; // UPLOADED, DISPATCHED, PROCESSING
}

function mapExtractionLabel(state: keyof typeof EXTRACTION_STATES): string {
  switch (state) {
    case 'ExtractionState.PROCESSED':
      return 'Processed';
    case 'ExtractionState.DERELICT':
      return 'Processed';
    case 'ExtractionState.FAILED':
      return 'Failed';
    case 'ExtractionState.INVALID':
      return 'Invalid';
    case 'ExtractionState.PROCESSING':
      return 'Processing';
    case 'ExtractionState.DISPATCHED':
      return 'Processing';
    case 'ExtractionState.UPLOADED':
      return 'Uploaded';
    default:
      return 'Unknown';
  }
}

function mapObservableToDocument(doc: ObservableDocumentModel): Document {
  const filename = doc.observableAsset?.filename || 'Unknown Document';
  const provider = doc.provider || '';
  const type = PROVIDER_LABELS[provider] || 'Document';
  const status = mapExtractionStatus(doc.extractionState);
  const statusLabel = mapExtractionLabel(doc.extractionState);
  const uploadDate = doc.createdAt ? formatShortDate(doc.createdAt) : '';

  return {
    id: doc.id ?? '',
    name: filename,
    type,
    status,
    uploadDate,
    statusLabel,
  };
}

export default class DocumentsIndex extends Component<DocumentsIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked selectedDocumentId: string | null = null;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get selectedObservableDocument(): ObservableDocumentModel | undefined {
    if (!this.selectedDocumentId) return undefined;
    return this.args.model.observableDocuments.find((d) => d.id === this.selectedDocumentId);
  }

  get selectedDocument(): Document | undefined {
    if (!this.selectedDocumentId) return undefined;
    return this.documents.find((d) => d.id === this.selectedDocumentId);
  }

  get isViewerOpen() {
    return !!this.selectedDocumentId;
  }

  viewDocument = (docId: string) => {
    this.selectedDocumentId = docId;
  };

  closeViewer = () => {
    this.selectedDocumentId = null;
  };

  get liveDocuments(): Document[] {
    return this.args.model.observableDocuments.map(mapObservableToDocument);
  }

  get hasLiveDocuments() {
    return this.liveDocuments.length > 0;
  }

  get documents() {
    if (this.isDemoMode) return DEMO_DOCUMENTS;
    return this.liveDocuments;
  }

  get showLoanUploads() {
    return this.reimaginedData.showStudentLoanRepayment || this.reimaginedData.showPslf;
  }

  get documentsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    if (this.hasLiveDocuments) return 'live';
    return 'placeholder';
  }

  get verifiedCount() {
    return this.documents.filter((d) => d.status === 'verified').length;
  }

  get pendingCount() {
    return this.documents.filter((d) => d.status === 'pending').length;
  }

  get actionNeededCount() {
    return this.documents.filter((d) => d.status === 'action-needed').length;
  }

  get smartActions(): SmartAction[] {
    return [
      {
        id: 'upload',
        title: 'Upload Document',
        description: 'Upload loan statements, tax forms, or other documents for verification.',
        icon: PhUploadSimple,
        priority: 'urgent',
        category: 'deadline',
        cta: 'Upload Now',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'review-pending',
        title: 'Review Pending',
        description: `You have ${this.pendingCount} document(s) awaiting review.`,
        icon: PhClock,
        priority: 'high',
        category: 'deadline',
        cta: 'Review Documents',
        route: 'authenticated.reimagined.advisor',
      },
      {
        id: 'connect-accounts',
        title: 'Connect Accounts',
        description: 'Instantly verify loan data by connecting your servicer accounts.',
        icon: PhShieldCheck,
        priority: 'medium',
        category: 'opportunity',
        cta: 'Connect Now',
        route: 'authenticated.reimagined.advisor',
      },
    ];
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <PageHero
        @gradientClass="bg-gradient-to-br from-violet-600 via-violet-700 to-purple-800"
        @decoration="circles"
        @rounded="2xl"
      >
        <div class="tio-row mb-4">
          <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
            <PhFileText class="h-7 w-7 text-white" />
          </div>
          <div>
            <Heading @level={{2}} class="text-white text-2xl font-bold">
              Document Center
            </Heading>
            <p class="tio-fg-on-brand text-sm">
              Upload, verify, and manage your financial documents
            </p>
          </div>
        </div>

        <DataIndicator @source={{this.documentsSource}} @dataSourceId="document-stats">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <GlassCard
              @label="Verified"
              @value={{this.verifiedCount}}
              @accent="emerald"
              @subtext="documents approved"
            />
            <GlassCard
              @label="Pending"
              @value={{this.pendingCount}}
              @accent="amber"
              @subtext="under review"
            />
            <GlassCard
              @label="Action Needed"
              @value={{this.actionNeededCount}}
              @accent="red"
              @subtext="require attention"
            />
          </div>
        </DataIndicator>
      </PageHero>

      {{! Smart Actions }}
      <SmartActionsSection
        @actions={{this.smartActions}}
        @title="Quick Actions"
        @subtitle="Manage your documents"
      />

      {{! Document List }}
      <SectionHeader
        @icon={{PhFileText}}
        @title="Your Documents"
        @subtitle="All uploaded and verified documents"
        @iconClass="tio-fg-brand"
      />
      <DataIndicator @source={{this.documentsSource}} @dataSourceId="document-list">
        {{#if this.documents.length}}
          <div class="space-y-3">
            {{#each this.documents as |doc|}}
              <Card
                @padding="sm"
                class="flex items-center justify-between hover:shadow-md transition-shadow"
              >
                <div class="tio-row-loose">
                  <div
                    class="flex h-12 w-12 items-center justify-center rounded-lg
                      {{if
                        (eq doc.status 'verified')
                        'tio-bg-success-soft dark:bg-green-900/30'
                        (if
                          (eq doc.status 'pending')
                          'tio-bg-warning-soft'
                          'tio-bg-danger-soft dark:bg-red-900/30'
                        )
                      }}"
                  >
                    {{#if (eq doc.status "verified")}}
                      <PhCheckCircle class="h-6 w-6 tio-fg-success" />
                    {{else if (eq doc.status "pending")}}
                      <PhClock class="h-6 w-6 tio-fg-warning" />
                    {{else}}
                      <PhWarning class="h-6 w-6 tio-fg-danger" />
                    {{/if}}
                  </div>
                  <div>
                    <div class="font-medium tio-fg-primary">{{doc.name}}</div>
                    <div class="tio-row-tight mt-1">
                      <Text class="tio-fg-muted text-xs">{{doc.type}}</Text>
                      <span class="text-gray-300">·</span>
                      <Text class="tio-fg-muted text-xs">{{doc.uploadDate}}</Text>
                    </div>
                  </div>
                </div>
                <div class="tio-row">
                  <Chip
                    @intent={{if
                      (eq doc.status "verified")
                      "success"
                      (if (eq doc.status "pending") "warning" "danger")
                    }}
                  >
                    {{doc.statusLabel}}
                  </Chip>
                  <button
                    type="button"
                    class="tio-fg-disabled hover:tio-fg-secondary p-2"
                    {{on "click" (fn this.viewDocument doc.id)}}
                  >
                    <PhEye class="h-5 w-5" />
                  </button>
                </div>
              </Card>
            {{/each}}
          </div>
        {{else}}
          <div class="bg-white rounded-xl border border-dashed tio-border-strong p-12 text-center">
            <div
              class="flex h-16 w-16 items-center justify-center rounded-full tio-bg-brand-soft mx-auto mb-4"
            >
              <PhFolderOpen class="h-8 w-8 tio-fg-brand" />
            </div>
            <Heading @level={{3}} class="text-lg font-semibold tio-fg-primary mb-2">
              No Documents Yet
            </Heading>
            <Text class="tio-fg-muted text-sm mb-6 max-w-md mx-auto">
              Upload your loan statements, NSLDS file, or other documents to unlock employer
              contributions and track your progress.
            </Text>
            {{#if this.showLoanUploads}}
              <LinkTo
                @route="authenticated.observability.upload"
                @query={{hash
                  source="reimagined-documents"
                  providers="nslds,statement"
                  returnTo="authenticated.reimagined.documents"
                }}
                class="tio-btn tio-btn--brand tio-btn--md"
              >
                <PhUploadSimple class="h-4 w-4" />
                Upload Documents
              </LinkTo>
            {{/if}}
          </div>
        {{/if}}
      </DataIndicator>

      {{! Upload Methods }}
      <SectionHeader
        @icon={{PhCloudArrowUp}}
        @title="Upload Methods"
        @subtitle="Choose how to share your documents"
        @iconClass="tio-fg-info"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card @padding="lg" class="text-center">
          <div
            class="flex h-14 w-14 items-center justify-center rounded-xl tio-bg-brand-soft mx-auto mb-4"
          >
            <PhCloudArrowUp class="h-7 w-7 tio-fg-brand" />
          </div>
          <Heading @level={{4}} class="font-semibold mb-2">File Upload</Heading>
          <Text class="tio-fg-secondary text-sm mb-4">
            Drag and drop or browse to upload PDF, JPG, or PNG documents.
          </Text>
          <Button @intent="primary" @appearance="outlined" class="w-full">
            Upload Files
          </Button>
        </Card>

        {{#if this.showLoanUploads}}
          <Card @padding="lg" class="text-center">
            <div
              class="flex h-14 w-14 items-center justify-center rounded-xl tio-bg-info-soft mx-auto mb-4"
            >
              <PhSparkle class="h-7 w-7 tio-fg-info" />
            </div>
            <Heading @level={{4}} class="font-semibold mb-2">Upload Documents</Heading>
            <Text class="tio-fg-secondary text-sm mb-4">
              Upload your loan statements or NSLDS file for automatic data verification.
            </Text>
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash
                source="reimagined-documents"
                providers="nslds,statement"
                returnTo="authenticated.reimagined.documents"
              }}
              class="block w-full rounded-md border border-blue-600 bg-white px-4 py-2 text-center text-sm font-medium tio-fg-info shadow-sm hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            >
              Upload Documents
            </LinkTo>
          </Card>
        {{/if}}

        <Card @padding="lg" class="text-center">
          <div
            class="flex h-14 w-14 items-center justify-center rounded-xl bg-teal-100 dark:bg-teal-900/30 mx-auto mb-4"
          >
            <PhRobot class="h-7 w-7 text-teal-600" />
          </div>
          <Heading @level={{4}} class="font-semibold mb-2">AI-Assisted</Heading>
          <Text class="tio-fg-secondary text-sm mb-4">
            Our AI reads and extracts data from your documents automatically.
          </Text>
          <Chip @intent="primary">Coming Soon</Chip>
        </Card>
      </div>

      {{! Document Viewer Drawer }}
      <Drawer @isOpen={{this.isViewerOpen}} @onClose={{this.closeViewer}} @size="lg">
        <:header>
          <div class="tio-row">
            <div class="p-2 rounded-lg tio-bg-brand-soft">
              <PhFileText class="h-5 w-5 tio-fg-brand" />
            </div>
            <div class="min-w-0 flex-1">
              <Heading @level={{3}} class="text-lg font-semibold tio-fg-primary truncate">
                {{#if this.selectedDocument}}
                  {{this.selectedDocument.name}}
                {{else}}
                  Document Details
                {{/if}}
              </Heading>
              {{#if this.selectedDocument}}
                <Text class="text-sm tio-fg-muted">{{this.selectedDocument.type}}</Text>
              {{/if}}
            </div>
            {{#if this.selectedDocument}}
              <Chip
                @intent={{if
                  (eq this.selectedDocument.status "verified")
                  "success"
                  (if (eq this.selectedDocument.status "pending") "warning" "danger")
                }}
              >
                {{this.selectedDocument.statusLabel}}
              </Chip>
            {{/if}}
          </div>
        </:header>
        <:body>
          {{#if this.selectedDocument}}
            {{! Document metadata }}
            <div class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <Text class="text-xs tio-fg-muted uppercase tracking-wide mb-1">Upload Date</Text>
                  <Text
                    class="text-sm font-medium tio-fg-primary"
                  >{{this.selectedDocument.uploadDate}}</Text>
                </div>
                <div>
                  <Text class="text-xs tio-fg-muted uppercase tracking-wide mb-1">Document Type</Text>
                  <Text
                    class="text-sm font-medium tio-fg-primary"
                  >{{this.selectedDocument.type}}</Text>
                </div>
                <div>
                  <Text class="text-xs tio-fg-muted uppercase tracking-wide mb-1">Status</Text>
                  <Text
                    class="text-sm font-medium tio-fg-primary"
                  >{{this.selectedDocument.statusLabel}}</Text>
                </div>
                <div>
                  <Text class="text-xs tio-fg-muted uppercase tracking-wide mb-1">Document ID</Text>
                  <Text class="text-sm font-mono tio-fg-muted">{{this.selectedDocument.id}}</Text>
                </div>
              </div>

              {{! Extraction summary for real documents }}
              {{#if this.selectedObservableDocument}}
                <div class="border-t tio-border-default pt-4 mt-4">
                  <Heading @level={{4}} class="text-sm font-semibold tio-fg-primary mb-3">
                    Extracted Data
                  </Heading>
                  {{#if (eq this.selectedDocument.status "verified")}}
                    <ExtractionSummary
                      @document={{this.selectedObservableDocument}}
                      class="w-full"
                    />
                  {{else if (eq this.selectedDocument.status "pending")}}
                    <div class="tio-row p-4 tio-bg-warning-soft rounded-lg">
                      <PhClock class="h-5 w-5 tio-fg-warning shrink-0" />
                      <p class="tio-fg-warning-body text-sm">
                        This document is being processed. Data will appear here once extraction is
                        complete.
                      </p>
                    </div>
                  {{else}}
                    <div class="tio-row p-4 tio-bg-danger-soft dark:bg-red-900/20 rounded-lg">
                      <PhWarning class="h-5 w-5 tio-fg-danger shrink-0" />
                      <Text class="text-sm tio-fg-danger-body dark:text-red-300">
                        We couldn't extract data from this document. Please try uploading again or
                        contact support.
                      </Text>
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/if}}
        </:body>
        <:footer>
          <div class="tio-row">
            {{#if this.selectedObservableDocument}}
              <a
                href={{this.selectedObservableDocument.observableAsset.signedUrl}}
                target="_blank"
                rel="noopener noreferrer"
                class="tio-btn tio-btn--brand tio-btn--md"
              >
                <PhDownloadSimple @size={{16}} />
                Download Original
                <PhArrowSquareOut @size={{14}} />
              </a>
            {{/if}}
            <button
              type="button"
              class="px-4 py-2 tio-fg-secondary border tio-border-strong rounded-lg hover:tio-bg-sunken transition-colors text-sm font-medium"
              {{on "click" this.closeViewer}}
            >
              Close
            </button>
          </div>
        </:footer>
      </Drawer>
    </VStack>
  </template>
}
